import { Loader2 } from './icons.tsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx.ts'

type Variant = 'primary' | 'secondary' | 'soft' | 'danger' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-on-fill hover:bg-primary-dark',
  secondary: 'bg-surface text-ink border border-border hover:bg-soft',
  // A quiet version of primary, for an action repeated on every row of a list.
  soft: 'bg-primary-light text-primary font-semibold hover:bg-primary/20',
  danger: 'bg-danger text-on-fill hover:bg-danger-dark',
  ghost: 'text-muted hover:bg-soft hover:text-ink',
}

const SIZES: Record<Size, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2 pos:min-h-12',
  lg: 'px-5 py-3 text-base gap-2 pos:min-h-12',
}

// A press pushes the button in a little and it springs back: the answer to a tap, which on a
// phone has no hover to show it landed.
const PRESS = 'transition-[color,background-color,border-color,scale] duration-150 ease-out'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
}

/** The Button look for something that is not a <button>, e.g. a router link that navigates. */
export function buttonClass({
  variant = 'primary',
  size = 'md',
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}): string {
  return cx(
    'inline-flex items-center justify-center rounded-xl font-medium',
    PRESS,
    'active:scale-[0.97]',
    VARIANTS[variant],
    SIZES[size],
    className,
  )
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-xl font-medium',
        PRESS,
        'enabled:active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  )
}

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-8 items-center justify-center tap-target rounded-xl text-muted transition-colors hover:bg-soft hover:text-ink enabled:active:translate-y-px disabled:opacity-40',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
